/**
 * Module dependencies.
 */

import { ReportMockup } from 'src/components/landingpage/components/report-mockup/report-mockup';
import { RewardsPointsInline } from 'src/components/landingpage/components/rewards-points-inline/rewards-points-inline';
import { useTranslation } from 'next-i18next';
import ArrowTag from 'src/assets/landingpage/arrow-tag.svg';
import Image from 'next/image';
import imgExclude from 'src/assets/landingpage/bg-desktop.webp';
import imgMobile from 'src/assets/landingpage/bg.webp';
import styled from 'styled-components';

/**
 * `Container` styled component.
 */

const Container = styled.div`
  /* stylelint-disable no-descending-specificity */
  align-items: flex-start;
  box-sizing: border-box;
  content: stretch;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 40px;
  margin-top: -16vw;
  overflow: hidden;
  padding: 0 0 64px;
  position: relative;
  width: 100%;

  &::before {
    background: #00cddc;
    bottom: 0;
    content: '';
    left: 0;
    position: absolute;
    right: 0;
    top: 25vw;
    z-index: 0;

    @media (max-width: 1024px) {
      top: 30vw;
    }
  }

  /* Background image */
  .background {
    align-items: flex-start;
    content: stretch;
    display: flex;
    flex-direction: column;
    gap: 8px;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 1;

    > div {
      aspect-ratio: 1440 / 814.753;
      left: 0;
      position: absolute;
      right: 0;
      top: 0;

      .background-image {
        display: block;
        height: 100%;
        width: 100%;

        img {
          display: block;
          height: 100%;
          max-width: none;
          object-fit: cover;
          width: 100%;
        }
      }

      .img-desktop {
        display: block;

        @media (max-width: 1024px) {
          display: none;
        }
      }

      .img-mobile {
        display: none;

        @media (max-width: 1024px) {
          display: block;
        }
      }
    }

    @media (max-width: 1440px) {
      left: calc(calc(1440px - 100vw) * -0.5);
      width: 1440px;
    }
  }

  .overlay {
    background: #00cddc;
    height: 200vw;
    left: 0;
    margin-bottom: -100px;
    position: absolute;
    top: 600px;
    transform: rotate(-14deg);
    transform-origin: center;
    width: 200vw;
    z-index: 13;

    @media (min-width: 1024px) {
      display: none;
    }
  }

  /* Content wrapper */
  .content {
    flex-shrink: 0;
    margin-top: 17vw;
    padding-bottom: 5vw;
    position: relative;
    width: 100%;
    z-index: 20;

    > div:first-child {
      height: 100%;
      margin-inline: auto;
      max-width: 1440px;
      width: 100%;

      > div {
        box-sizing: border-box;
        display: grid;
        grid-template-areas:
          'title subtitle'
          'title image'
          'price action';
        grid-template-columns: 2fr 1fr;
        grid-template-rows: auto auto auto auto;
        height: auto;
        position: relative;
        width: 100%;

        @media (min-width: 1024px) {
          margin-inline: auto;
          max-width: 1056px;
        }

        @media (max-width: 1119px) {
          padding: 0 32px;
        }

        @media (max-width: 1023px) {
          grid-template-areas:
            'title subtitle'
            'title image'
            'price price'
            'action action';
          grid-template-rows: auto auto auto auto;
        }

        @media (max-width: 767px) {
          grid-template-areas:
            'title'
            'subtitle'
            'image'
            'price'
            'action';
          grid-template-columns: 1fr;
          grid-template-rows: auto auto auto auto auto;
        }
      }
    }
  }

  /* Headline */
  .headline {
    align-self: flex-start;
    flex-shrink: 0;
    grid-area: title;
    position: relative;

    @media (max-width: 767px) {
      margin-top: 16px;
      padding-bottom: 110px;
    }

    > div {
      align-items: center;
      display: flex;
      flex-direction: row;
      height: 100%;
      justify-content: center;
      width: 100%;

      > div {
        align-items: center;
        box-sizing: border-box;
        content: stretch;
        display: flex;
        gap: 8px;
        justify-content: center;
        padding: 56px 116px 0 0;
        position: relative;
        width: 100%;

        @media (max-width: 767px) {
          padding: 0;
        }

        p {
          color: white;
          flex-basis: 0;
          flex-grow: 1;
          flex-shrink: 0;
          font-family: Raleway, sans-serif;
          font-size: 42px;
          font-weight: 900;
          line-height: 44px;
          min-height: 1px;
          min-width: 1px;
          position: relative;
        }
      }
    }
  }

  /* Subheadline */
  .subheadline {
    align-self: flex-start;
    flex-shrink: 0;
    grid-area: subtitle;
    margin-left: 16px;
    position: relative;

    @media (max-width: 767px) {
      margin-left: 16px;
      margin-right: auto;
      margin-top: 24px;
      max-width: 375px;
      width: 100%;
    }

    > div {
      align-items: center;
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;

      > div {
        align-items: center;
        box-sizing: border-box;
        content: stretch;
        display: flex;
        gap: 8px;
        justify-content: center;
        padding: 8px;
        position: relative;
        width: 100%;
      }
    }

    p {
      color: white;
      flex-basis: 0;
      flex-grow: 1;
      flex-shrink: 0;
      font-family: Raleway, sans-serif;
      font-size: 24px;
      font-weight: 900;
      line-height: 32px;
      min-height: 1px;
      min-width: 1px;
      position: relative;

      @media (max-width: 767px) {
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
      }
    }
  }

  /* Image section */
  .image {
    align-self: flex-start;
    flex-shrink: 0;
    grid-area: image;
    position: relative;

    @media (max-width: 767px) {
      display: flex;
      justify-content: center;
      margin-top: 24px;
    }

    > div {
      align-items: center;
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;

      > div {
        align-items: center;
        box-sizing: border-box;
        content: stretch;
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 8px;
        position: relative;
        width: 100%;

        @media (max-width: 767px) {
          padding: 0;
        }
      }
    }
  }

  /* Pricing section */
  .pricing {
    align-items: stretch;
    align-self: flex-start;
    content: stretch;
    display: flex;
    flex-shrink: 0;
    gap: 32px;
    grid-area: price;
    justify-content: center;
    justify-self: end;
    max-width: 704px;
    overflow: clip;
    position: relative;

    @media (max-width: 1023px) {
      justify-self: center;
      margin-top: 32px;
    }

    @media (max-width: 767px) {
      margin-top: 24px;
      max-width: 100%;
      width: 100%;
    }
  }

  /* Price tag */
  .price-tag {
    align-items: flex-start;
    content: stretch;
    display: flex;
    flex-basis: 0;
    flex-direction: column;
    flex-grow: 1;
    flex-shrink: 0;
    min-height: 1px;
    min-width: 1px;
    position: relative;

    @media (max-width: 767px) {
      min-width: 0;
      width: 100%;
    }

    .tag-top {
      align-items: stretch;
      display: flex;
      flex-direction: row;
      width: 100%;
    }

    .price-tag-info {
      box-sizing: border-box;
      color: rgba(10, 10, 40, 0.85);
      font-family: Raleway, sans-serif;
      font-size: 12px;
      font-weight: 500;
      line-height: 18px;
      margin: 8px 0 0;
      max-width: 100%;
      width: 100%;

      @media (min-width: 1024px) {
        max-width: 366px;
      }

      @media (max-width: 767px) {
        padding-right: 16px;
      }

      @media (max-width: 375px) {
        font-size: 11px;
        line-height: 16px;
      }
    }

    .tag-body {
      align-self: stretch;
      background: #0a0a28;
      flex-basis: 0;
      flex-grow: 1;
      flex-shrink: 0;
      min-height: 1px;
      min-width: 1px;
      position: relative;

      @media (max-width: 767px) {
        min-width: 0;
      }

      > div {
        height: 100%;
        width: 100%;

        > div {
          align-items: flex-start;
          box-sizing: border-box;
          content: stretch;
          display: flex;
          flex-direction: column;
          height: 100%;
          padding: 16px 0 16px 32px;
          position: relative;
          width: 100%;

          @media (max-width: 767px) {
            padding: 16px;
          }
        }
      }
    }

    .tag-arrow {
      align-self: stretch;
      flex-shrink: 0;
      position: relative;
      width: 61.998px;

      @media (max-width: 1023px) {
        display: none;
      }

      svg {
        display: block;
        height: 100%;
        width: 100%;
      }
    }
  }

  /* Price content */
  .price-content {
    align-items: center;
    content: stretch;
    display: flex;
    flex-shrink: 0;
    gap: 32px;
    position: relative;
    width: 100%;

    @media (max-width: 767px) {
      gap: 16px;
    }

    @media (max-width: 374px) {
      align-items: center;
      flex-direction: column;
    }

    > p {
      color: white;
      flex-basis: 0;
      flex-grow: 1;
      flex-shrink: 0;
      font-family: Raleway, sans-serif;
      font-size: 24px;
      font-weight: 900;
      line-height: 32px;
      max-width: 704px;
      min-height: 1px;
      min-width: 260px;
      position: relative;

      @media (max-width: 767px) {
        flex-shrink: 1;
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
        min-width: 0;
      }

      @media (max-width: 374px) {
        min-height: initial;
        text-align: center;
        width: 100%;
      }
    }

    .divider {
      align-items: center;
      display: flex;
      flex-shrink: 0;
      height: calc(1px * ((var(--transform-inner-width) * 1) + (var(--transform-inner-height) * 0)));
      justify-content: center;
      position: relative;
      width: calc(1px * ((var(--transform-inner-height) * 1) + (var(--transform-inner-width) * 0)));

      @media (max-width: 374px) {
        background-color: white;
        height: 1px;
        width: 100%;
      }

      > div {
        flex: none;
        transform: rotate(90deg);

        @media (max-width: 374px) {
          display: none;
        }

        > div {
          height: 7.081e-6px;
          position: relative;
          width: 71px;

          > div {
            bottom: -1px;
            left: 0;
            position: absolute;
            right: 0;
            top: -1px;

            svg {
              display: block;
              height: 100%;
              width: 100%;
            }
          }
        }
      }
    }
  }

  /* Price value */
  .price-value {
    align-items: center;
    content: stretch;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    justify-content: center;
    padding-right: 32px;
    position: relative;
    text-align: center;
    width: 126px;

    @media (max-width: 767px) {
      padding-right: 0;
      width: auto;
    }

    @media (max-width: 374px) {
      width: 100%;
    }

    .amount {
      color: #00cddc;
      flex-shrink: 0;
      font-family: Raleway, sans-serif;
      font-size: 42px;
      font-weight: 900;
      line-height: 44px;
      min-width: 100%;
      position: relative;
      width: min-content;
    }

    .vat {
      color: white;
      flex-shrink: 0;
      font-family: Raleway, sans-serif;
      font-size: 16px;
      font-weight: 700;
      line-height: 24px;
      position: relative;
      text-wrap: nowrap;
      white-space: pre;
    }
  }

  /* Button wrapper */
  .button-wrapper {
    align-items: center;
    display: flex;
    flex-shrink: 0;
    grid-area: action;
    justify-content: center;
    position: relative;

    @media (max-width: 1023px) {
      margin-top: 16px;
    }

    @media (max-width: 767px) {
      margin-top: 24px;
    }

    > div {
      align-items: center;
      display: flex;
      flex-direction: column;
      justify-content: center;
      width: 100%;

      > div {
        align-items: center;
        box-sizing: border-box;
        content: stretch;
        display: flex;
        flex-direction: column;
        gap: 8px;
        justify-content: center;
        padding: 8px;
        position: relative;
        width: 100%;
      }
    }
  }

  /* Schedule button */
  a {
    align-items: center;
    background: white;
    border: 0;
    box-sizing: border-box;
    content: stretch;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    justify-content: center;
    padding: 24px;
    position: relative;
    text-decoration: none;

    &:hover {
      background: #0a0a28;

      > div {
        color: white;
      }
    }

    > div {
      color: #0a0a28;
      display: flex;
      flex-direction: column;
      flex-shrink: 0;
      font-family: Raleway, sans-serif;
      font-size: 16px;
      font-weight: 900;
      justify-content: center;
      line-height: 0;
      position: relative;
      text-align: center;
      text-transform: uppercase;
      text-wrap: nowrap;

      p {
        line-height: 24px;
        white-space: pre;
      }
    }
  }
  /* stylelint-enable no-descending-specificity */
`;

/**
 * Export `ProductSection` component.
 */

export function ProductSection() {
  const { t } = useTranslation('landingpage');

  return (
    <Container
      data-name={'section'}
      id={'section-produto'}
      style={{
        opacity: 0,
        transform: 'translateY(400px)',
        transition: 'opacity 0.1s ease-out, transform 0.1s ease-out'
      }}
    >
      <div className={'background'}>
        <div data-name={'Exclude'}>
          <Image
            alt={'Elemento decorativo de fundo'}
            className={'background-image img-desktop'}
            fill
            priority
            sizes={'100vw'}
            src={imgExclude}
            style={{ objectFit: 'cover' }}
          />

          <Image
            alt={'Elemento decorativo de fundo'}
            className={'background-image img-mobile'}
            fill
            sizes={'100vw'}
            src={imgMobile}
            style={{ objectFit: 'cover' }}
          />
        </div>
      </div>

      <div className={'overlay'} />

      <div className={'content'} data-name={'section'}>
        <div>
          <div>
            <div className={'headline'} data-name={'text'}>
              <div>
                <div>
                  <p>{t('product.headline')}</p>
                </div>
              </div>
            </div>

            <div className={'subheadline'} data-name={'text'}>
              <div>
                <div>
                  <p>{t('product.subheadline')}</p>
                </div>
              </div>
            </div>

            <div className={'image'}>
              <div>
                <div>
                  <ReportMockup />
                </div>
              </div>
            </div>

            <div className={'pricing'} data-name={'pricing'}>
              <div className={'price-tag'} data-name={'price-tag'}>
                <div className={'tag-top'} data-name={'tag-top'}>
                  <div className={'tag-body'} data-name={'left-container'}>
                    <div>
                      <div>
                        <div className={'price-content'} data-name={'Copy + preco'}>
                          <p>{t('product.pricing.text')}</p>

                          <div
                            className={'divider'}
                            style={
                              {
                                '--transform-inner-height': '0',
                                '--transform-inner-width': '71'
                              } as React.CSSProperties
                            }
                          >
                            <div>
                              <div>
                                <div>
                                  <svg fill={'none'} preserveAspectRatio={'none'} viewBox={'0 0 71 2'}>
                                    <path
                                      d={'M0 1.00001L71 1'}
                                      id={'Line 9'}
                                      stroke={'var(--stroke-0, white)'}
                                      strokeWidth={'2'}
                                    />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className={'price-value'} data-name={'preco'}>
                            <p className={'amount'}>{t('product.pricing.price')}</p>
                            <p className={'vat'}>{t('product.pricing.vat')}</p>

                            <RewardsPointsInline label={t('product.pricing.points')} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={'tag-arrow'} data-name={'Vector'}>
                    <ArrowTag />
                  </div>
                </div>

                <p className={'price-tag-info'} data-name={'info'} id={'product-price-tag-info'}>
                  {t('product.pricing.info')}
                </p>
              </div>
            </div>

            <div className={'button-wrapper'}>
              <div>
                <div>
                  <a aria-label={t('product.button')} data-name={'BTN'} href={'/agendar?canal=santander'}>
                    <div>
                      <p>{t('product.button')}</p>
                    </div>
                  </a>

                  <RewardsPointsInline
                    id={'product-cta-points'}
                    label={t('product.pricing.points')}
                    placement={'ctaBelow'}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
}
